import { ChevronRight, CheckCircle2, UtensilsCrossed, Clock } from "lucide-react";
import HealthSourceInfo from "@/components/health/HealthSourceInfo";
import { useTimeFormatter } from "@/lib/timeFormat";

// Map meal name to emoji thumbnail
function mealEmoji(name = "") {
  const n = name.toLowerCase();
  if (/breakfast|egg|oat|cereal|pancake/.test(n)) return "🍳";
  if (/mid.*morning|snack|nut|almond/.test(n)) return "🥜";
  if (/lunch|rice|bowl|beef|chicken/.test(n)) return "🍚";
  if (/pre.?workout|shake|whey|protein/.test(n)) return "🥤";
  if (/post.?workout/.test(n)) return "🥗";
  if (/dinner|salmon|fish|steak/.test(n)) return "🍱";
  if (/fruit|banana|apple/.test(n)) return "🍌";
  return "🍽️";
}

export default function YourNextMealCard({
  nextMeal,
  allMealsLogged = false,
  totalMealsCount = 0,
  onOpenMeal,
}) {
  const fmtTime = useTimeFormatter();

  // If all meals logged for today
  if (allMealsLogged && totalMealsCount > 0) {
    return (
      <div
        data-testid="your-next-meal-card"
        className="rounded-3xl p-5 text-white shadow-lg border border-emerald-500/30 flex items-center justify-between gap-4"
        style={{
          background: "radial-gradient(ellipse at top left, #0A241C 0%, #06130F 100%)",
        }}
      >
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
              NUTRITION COMPLETE
            </p>
            <h4 className="text-base font-display font-bold text-white">
              All meals logged for today!
            </h4>
            <p className="text-xs text-slate-300">
              Great consistency keeping your daily nutrition on target.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If no planned meals scheduled
  if (!nextMeal) {
    return (
      <div
        data-testid="your-next-meal-card"
        className="rounded-3xl p-5 text-white shadow-lg border border-white/10 flex items-center justify-between gap-4"
        style={{
          background: "radial-gradient(ellipse at top, #101B2E 0%, #080F1D 100%)",
        }}
      >
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
            <UtensilsCrossed className="w-5 h-5 text-slate-400" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-sky-400">
              YOUR NEXT MEAL
            </p>
            <h4 className="text-base font-display font-bold text-white truncate">
              No upcoming scheduled meal
            </h4>
            <p className="text-xs text-slate-400 truncate">
              Tap preview to browse your daily meal plan or log food.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Compute ingredients summary
  const foods = nextMeal.foods || [];
  const ingredientSummary = foods.length > 0
    ? foods.map((f) => f.food_name).slice(0, 3).join(" · ")
    : "Planned healthy meal";

  const mealCalories = foods.reduce(
    (sum, f) => sum + (Number(f.calories) || 0),
    0
  );

  return (
    <div
      data-testid="your-next-meal-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpenMeal(nextMeal.meal_name)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenMeal(nextMeal.meal_name);
        }
      }}
      className="rounded-3xl p-4 sm:p-5 text-white shadow-xl border border-white/10 hover:border-sky-400/40 transition-all cursor-pointer group select-none"
      style={{
        background: "radial-gradient(ellipse at top left, #111D33 0%, #070D18 100%)",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-sky-400">
          YOUR NEXT MEAL
        </p>
        {nextMeal.time && (
          <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3 text-slate-500" />
            {fmtTime(nextMeal.time)}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl shrink-0 group-hover:scale-105 transition-transform">
            {mealEmoji(nextMeal.meal_name)}
          </div>
          <div className="min-w-0">
            <h4 className="text-base sm:text-lg font-display font-bold text-white truncate">
              {nextMeal.meal_name}
            </h4>
            <p className="text-xs text-slate-300 truncate">
              {ingredientSummary}
            </p>
            <div className="flex items-center gap-2 mt-0.5 text-xs">
              <span className="font-extrabold text-sky-400 font-mono">
                {mealCalories} kcal<HealthSourceInfo topic="food" />
              </span>
              {nextMeal.time && (
                <>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-400 font-medium">
                    {fmtTime(nextMeal.time)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-sky-500/20 group-hover:text-sky-300 border border-white/10 flex items-center justify-center text-slate-400 shrink-0 transition-all">
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}
